// A panel docked to the right on a wide screen, and the handle that brings it
// back: shared by Prime beside a page (PrimePanel) and the artifacts beside the
// lead's thread (ArtifactPanel), docs/prime-panel.md. Drag the left edge to
// resize (the arrow keys do the same on the focused edge); the width is kept
// between PANEL_MIN and the most the window allows (clampWidth).
import { useEffect, useRef, type ReactNode } from 'react'
import { clampWidth, PANEL_MIN } from '@/lib/panel'
import { useDocked } from '@/state/panel'
import { cx } from './ui'

export const DockAside = ({
  width,
  onWidth,
  label,
  testId,
  className,
  children,
}: {
  width: number
  onWidth: (w: number) => void
  /** what the panel is, for assistive tech ("Prime", "Artifacts") */
  label: string
  testId: string
  className?: string
  children: ReactNode
}) => {
  const drag = useRef<{ x: number; w: number } | null>(null)
  const resize = (w: number) => onWidth(clampWidth(w, window.innerWidth))
  const shown = clampWidth(width, window.innerWidth)
  // what floats over the page (a thread's scroll buttons) keeps clear of the
  // panel: its width and the shell's inset beside it
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--dock-w', `${shown + 32}px`)
    return () => {
      root.style.removeProperty('--dock-w')
    }
  }, [shown])
  // with a panel docked the page takes the whole width, so the thread or the
  // editor beside it is not squeezed into the usual column
  useEffect(() => {
    useDocked.getState().set(true)
    return () => useDocked.getState().set(false)
  }, [])
  return (
    <aside
      className={cx(
        // the height leaves room for the shell around it (its margin and main's
        // padding: about 1rem above and 2.5rem below), so sticky top-4 holds
        // at the very bottom of the page and on a thread shorter than the
        // window, where a taller panel was pushed up past the window's top
        'relative shrink-0 sticky top-4 flex h-[calc(100dvh-3.75rem)] flex-col border-l border-line bg-ink',
        className,
      )}
      style={{ width: shown }}
      aria-label={label}
      data-testid={testId}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize the ${label} panel`}
        aria-valuemin={PANEL_MIN}
        aria-valuenow={shown}
        tabIndex={0}
        className="absolute -left-1 top-0 bottom-0 z-10 w-2 cursor-col-resize touch-none hover:bg-line focus-visible:bg-line"
        data-testid={`${testId}-resize`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId)
          drag.current = { x: e.clientX, w: shown }
        }}
        onPointerMove={(e) => {
          if (drag.current) resize(drag.current.w + (drag.current.x - e.clientX))
        }}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') resize(shown + 24)
          else if (e.key === 'ArrowRight') resize(shown - 24)
        }}
      />
      {children}
    </aside>
  )
}

/** The tab at the right edge that brings a hidden panel back. */
export const DockHandle = ({
  onClick,
  label,
  title,
  testId,
  children,
}: {
  onClick: () => void
  label: string
  title: string
  testId: string
  children: ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    className="fixed right-0 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1 rounded-l-full border border-r-0 border-line bg-ink text-muted hover:text-fg pl-2.5 pr-1.5 py-2.5 text-xs"
    aria-label={label}
    title={title}
    data-testid={testId}
  >
    {children}
  </button>
)
